import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Shield, 
  Download, 
  Copy, 
  ArrowDownToLine, 
  ArrowUpFromLine, 
  Check, 
  Loader2, 
  AlertTriangle,
  Wallet,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ZKVault() {
  // ── ALL HOOKS FIRST — no early returns before this block ──
  const { isSubUser } = useAuth();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showPhantomWithdrawModal, setShowPhantomWithdrawModal] = useState(false);
  const [zkNote, setZkNote] = useState(null);
  const [withdrawResult, setWithdrawResult] = useState(null);
  const [evmWithdrawResult, setEvmWithdrawResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [walletBalances, setWalletBalances] = useState({});
  const [treasuryInfo, setTreasuryInfo] = useState(null);
  const [phantomConnected, setPhantomConnected] = useState(false);
  const [phantomAddress, setPhantomAddress] = useState('');
  const [metamaskConnected, setMetamaskConnected] = useState(false);
  const [metamaskAddress, setMetamaskAddress] = useState('');
  const [selectedWalletType, setSelectedWalletType] = useState('phantom');
  const [manualAddress, setManualAddress] = useState('');

  const [depositForm, setDepositForm] = useState({ amount: 100, token: 'USDT' });
  const [withdrawForm, setWithdrawForm] = useState({ zk_note: '', recipient_address: '' });
  const [phantomWithdrawForm, setPhantomWithdrawForm] = useState({ zk_note: '', network: 'devnet', crypto: 'SOL' });

  const cryptoRates = {
    SOL: { rate: 0.005, symbol: 'SOL', name: 'Solana' },
    USDT: { rate: 1, symbol: 'USDT', name: 'Tether USD' },
    USDC: { rate: 1, symbol: 'USDC', name: 'USD Coin' },
    ETH: { rate: 0.00025, symbol: 'ETH', name: 'Ethereum' }
  };

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/zk/history`);
      setDeposits(data.deposits);
    } catch (error) {
      console.error('Failed to fetch ZK history:', error);
    }
  }, []);

  const fetchWalletBalance = useCallback(async (address) => {
    if (!address) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/wallet/balance/${encodeURIComponent(address)}`);
      setWalletBalances(prev => ({ ...prev, [address]: data }));
    } catch (error) {
      console.error('Failed to fetch wallet balance:', error);
    }
  }, []);

  const fetchTreasury = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/solana/treasury`);
      setTreasuryInfo(data);
    } catch (error) {
      console.error('Failed to fetch treasury:', error);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchTreasury();
  }, [fetchHistory, fetchTreasury]);

  useEffect(() => {
    if (phantomConnected && phantomAddress) fetchWalletBalance(phantomAddress);
    if (metamaskConnected && metamaskAddress) fetchWalletBalance(metamaskAddress);
  }, [phantomConnected, phantomAddress, metamaskConnected, metamaskAddress, fetchWalletBalance]);

  // ── GUARD after all hooks ──
  if (isSubUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 mx-auto mb-3 text-[hsl(215,16%,40%)] opacity-50" />
          <p className="text-[hsl(215,16%,56%)]">Access restricted to administrators.</p>
        </div>
      </div>
    );
  }

  // ── HANDLERS (only reached by admin) ──

  const connectPhantom = async () => {
    try {
      const isPhantomInstalled = typeof window !== 'undefined' && window.solana && window.solana.isPhantom;
      if (isPhantomInstalled) {
        try {
          const response = await window.solana.connect({ onlyIfTrusted: false });
          const address = response.publicKey.toString();
          setPhantomAddress(address);
          setPhantomConnected(true);
          setSelectedWalletType('phantom');
          toast.success(`Phantom Connected (Live): ${address.slice(0, 6)}...${address.slice(-4)}`);
          return;
        } catch (phantomError) {
          console.log('Phantom rejected, using demo:', phantomError);
        }
      }
      const chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
      const demoAddress = Array.from({ length: 44 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
      setPhantomAddress(demoAddress);
      setPhantomConnected(true);
      setSelectedWalletType('phantom');
      toast.success(`Phantom Connected (Demo): ${demoAddress.slice(0, 6)}...${demoAddress.slice(-4)}`);
      try {
        await axios.post(`${API_URL}/api/wallet/connect`, { chain: 'solana', address: demoAddress, wallet_type: 'phantom' });
      } catch (err) { /* ignore */ }
    } catch (error) {
      toast.error('Failed to connect Phantom.');
    }
  };

  const connectMetaMask = async () => {
    try {
      if (typeof window !== 'undefined' && window.ethereum) {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
          if (accounts && accounts.length > 0) {
            const address = accounts[0];
            setMetamaskAddress(address);
            setMetamaskConnected(true);
            setSelectedWalletType('metamask');
            toast.success(`MetaMask Connected (Live): ${address.slice(0, 6)}...${address.slice(-4)}`);
            return;
          }
        } catch (mmError) {
          console.log('MetaMask rejected, using demo:', mmError);
        }
      }
      const demoAddress = '0x' + Array.from({ length: 40 }, () => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join('');
      setMetamaskAddress(demoAddress);
      setMetamaskConnected(true);
      setSelectedWalletType('metamask');
      toast.success(`MetaMask Connected (Demo): ${demoAddress.slice(0, 6)}...${demoAddress.slice(-4)}`);
      try {
        await axios.post(`${API_URL}/api/wallet/connect`, { chain: 'evm', address: demoAddress, wallet_type: 'metamask' });
      } catch (err) { /* ignore */ }
    } catch (error) {
      toast.error('Failed to connect MetaMask.');
    }
  };

  const connectManualWallet = (address) => {
    if (!address || address.length < 10) { toast.error('Please enter a valid wallet address'); return; }
    if (address.startsWith('0x')) {
      setMetamaskAddress(address); setMetamaskConnected(true); setSelectedWalletType('metamask');
      toast.success(`EVM Wallet Added: ${address.slice(0, 6)}...${address.slice(-4)}`);
    } else {
      setPhantomAddress(address); setPhantomConnected(true); setSelectedWalletType('phantom');
      toast.success(`Solana Wallet Added: ${address.slice(0, 6)}...${address.slice(-4)}`);
    }
    setManualAddress('');
  };

  const getConnectedAddress = () => {
    if (selectedWalletType === 'metamask' && metamaskConnected) return metamaskAddress;
    if (selectedWalletType === 'phantom' && phantomConnected) return phantomAddress;
    return null;
  };

  const isAnyWalletConnected = () => phantomConnected || metamaskConnected;

  const handleDeposit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/zk/deposit`, depositForm);
      setZkNote(data.zk_note);
      toast.success('Deposit successful! Save your ZK-Note securely.');
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deposit failed');
    }
    setLoading(false);
  };

  const handleWithdraw = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/zk/withdraw`, withdrawForm);
      setEvmWithdrawResult(data);
      toast.success(`Withdrawal confirmed! $${data.net_amount.toFixed(2)} sent.`);
      fetchHistory();
      if (data.recipient) fetchWalletBalance(data.recipient);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    }
    setLoading(false);
  };

  const handlePhantomWithdraw = async () => {
    const walletAddress = getConnectedAddress();
    if (!walletAddress) { toast.error('Please connect a wallet first'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/zk/withdraw-to-phantom`, {
        zk_note: phantomWithdrawForm.zk_note,
        phantom_address: walletAddress,
        network: phantomWithdrawForm.network,
        crypto: phantomWithdrawForm.crypto
      });
      setWithdrawResult(data);
      toast.success(`Withdrawal successful! ${data.crypto_amount} ${data.crypto}`);
      fetchHistory();
      if (walletAddress) fetchWalletBalance(walletAddress);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    }
    setLoading(false);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard');
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try { document.execCommand('copy'); setCopied(true); toast.success('Copied to clipboard'); }
      catch { toast.error('Failed to copy. Please copy manually.'); }
      document.body.removeChild(textArea);
    }
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadZkNote = () => {
    const content = `GauriLabs ZK-Note\n\nNote: ${zkNote}\nGenerated: ${new Date().toISOString()}\n\nWARNING: Keep this note private. Anyone with this note can withdraw your funds.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gaurilabs-zk-note-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('ZK-Note downloaded');
  };

  const activeDeposits = deposits.filter(d => d.status === 'deposited');
  const totalDeposited = activeDeposits.reduce((sum, d) => sum + d.amount, 0);

  return (
    <div className="space-y-6" data-testid="zk-vault-page">
      <div>
        <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
          <Shield className="w-7 h-7 text-[#E8520F]" />
          ZK-Privacy Vault
        </h1>
        <p className="text-[hsl(215,16%,56%)]">Deposit and withdraw anonymously via ZK-proofs • Supports Phantom (Solana) & MetaMask (EVM)</p>
      </div>

      {/* Wallet Connection */}
      <div className="solar-card p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-white">Connect Wallet</h3>
          {isAnyWalletConnected() && (
            <span className="text-xs px-2 py-1 bg-[#10B981]/10 text-[#10B981] rounded-full">
              {selectedWalletType === 'metamask' ? 'EVM' : 'Solana'} Connected
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Phantom */}
          <div className={`p-3 rounded-lg border ${phantomConnected ? 'border-[#10B981] bg-[#10B981]/5' : 'border-[hsl(220,16%,18%)]'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full ${phantomConnected ? 'bg-gradient-to-br from-[#AB9FF2] to-[#6366F1]' : 'bg-gray-200'} flex items-center justify-center`}>
                  <span className="text-white text-xs font-bold">P</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Phantom</p>
                  <p className="text-xs text-[hsl(215,16%,56%)]">Solana</p>
                </div>
              </div>
              {phantomConnected && <Check className="w-4 h-4 text-[#10B981]" />}
            </div>
            {phantomConnected ? (
              <div>
                <p className="text-xs text-[hsl(215,16%,56%)] font-mono truncate">{phantomAddress}</p>
                {walletBalances[phantomAddress]?.balance > 0 && (
                  <p className="text-sm font-bold text-[#10B981] mt-1">Balance: ${walletBalances[phantomAddress].balance.toFixed(2)}</p>
                )}
              </div>
            ) : (
              <Button onClick={connectPhantom} size="sm" className="w-full bg-gradient-to-r from-[#AB9FF2] to-[#6366F1] text-white text-xs">
                Connect Phantom
              </Button>
            )}
          </div>
          {/* MetaMask */}
          <div className={`p-3 rounded-lg border ${metamaskConnected ? 'border-[#10B981] bg-[#10B981]/5' : 'border-[hsl(220,16%,18%)]'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full ${metamaskConnected ? 'bg-gradient-to-br from-[#F6851B] to-[#E2761B]' : 'bg-gray-200'} flex items-center justify-center`}>
                  <span className="text-white text-xs font-bold">M</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-white">MetaMask</p>
                  <p className="text-xs text-[hsl(215,16%,56%)]">EVM/Ethereum</p>
                </div>
              </div>
              {metamaskConnected && <Check className="w-4 h-4 text-[#10B981]" />}
            </div>
            {metamaskConnected ? (
              <div>
                <p className="text-xs text-[hsl(215,16%,56%)] font-mono truncate">{metamaskAddress}</p>
                {walletBalances[metamaskAddress]?.balance > 0 && (
                  <p className="text-sm font-bold text-[#10B981] mt-1">Balance: ${walletBalances[metamaskAddress].balance.toFixed(2)}</p>
                )}
              </div>
            ) : (
              <Button onClick={connectMetaMask} size="sm" className="w-full bg-gradient-to-r from-[#F6851B] to-[#E2761B] text-white text-xs">
                Connect MetaMask
              </Button>
            )}
          </div>
        </div>
        <div className="pt-3 border-t border-[hsl(220,16%,18%)]">
          <p className="text-xs text-[hsl(215,16%,56%)] mb-2">Or enter wallet address manually:</p>
          <div className="flex gap-2">
            <Input value={manualAddress} onChange={(e) => setManualAddress(e.target.value)} placeholder="0x... (EVM) or Solana address" className="text-xs font-mono border-[hsl(220,16%,18%)]" />
            <Button onClick={() => connectManualWallet(manualAddress)} variant="outline" size="sm" className="border-[hsl(220,16%,18%)] whitespace-nowrap">Add Wallet</Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="zk-vault-card flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#E8520F]/10 flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#E8520F]" />
          </div>
          <div>
            <p className="text-sm text-[hsl(215,16%,56%)]">Active Deposits</p>
            <p className="text-2xl font-bold text-white">{activeDeposits.length}</p>
          </div>
        </div>
        <div className="zk-vault-card flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
            <ArrowDownToLine className="w-6 h-6 text-[#10B981]" />
          </div>
          <div>
            <p className="text-sm text-[hsl(215,16%,56%)]">Total Deposited</p>
            <p className="text-2xl font-bold text-white">${totalDeposited.toLocaleString()}</p>
          </div>
        </div>
        <div className="zk-vault-card flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#00CED1]/10 flex items-center justify-center">
            <span className="text-[#00CED1] font-bold text-sm">0.5%</span>
          </div>
          <div>
            <p className="text-sm text-[hsl(215,16%,56%)]">Protocol Fee</p>
            <p className="text-2xl font-bold text-white">Gas-less</p>
          </div>
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardHeader>
            <CardTitle className="text-white font-['Outfit'] flex items-center gap-2">
              <ArrowDownToLine className="w-5 h-5 text-[#10B981]" />
              Deposit
            </CardTitle>
            <CardDescription>Deposit tokens and receive a ZK-Note</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setShowDepositModal(true)} className="w-full bg-[#10B981] hover:bg-[#059669] text-white">
              <ArrowDownToLine className="w-4 h-4 mr-2" />Make Deposit
            </Button>
          </CardContent>
        </Card>

        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardHeader>
            <CardTitle className="text-white font-['Outfit'] flex items-center gap-2">
              <ArrowUpFromLine className="w-5 h-5 text-[#00CED1]" />
              Withdraw (EVM)
            </CardTitle>
            <CardDescription>Withdraw to any EVM address</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setShowWithdrawModal(true)} className="w-full bg-[#00CED1] hover:bg-[#00B3B3] text-white">
              <ArrowUpFromLine className="w-4 h-4 mr-2" />Withdraw
            </Button>
          </CardContent>
        </Card>

        <Card className={`border-[hsl(220,16%,18%)] shadow-none ${isAnyWalletConnected() ? 'bg-gradient-to-br from-[#10B981]/5 to-[#E8520F]/5' : ''}`}>
          <CardHeader>
            <CardTitle className="text-white font-['Outfit'] flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#E8520F]" />
              Withdraw to Wallet
            </CardTitle>
            <CardDescription>{isAnyWalletConnected() ? `To ${selectedWalletType === 'metamask' ? 'MetaMask (EVM)' : 'Phantom (Solana)'}` : 'Connect a wallet first'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setShowPhantomWithdrawModal(true)} disabled={!isAnyWalletConnected()} className="w-full bg-gradient-to-r from-[#E8520F] to-[#6366F1] hover:opacity-90 text-white">
              <Wallet className="w-4 h-4 mr-2" />Withdraw to {isAnyWalletConnected() ? (selectedWalletType === 'metamask' ? 'MetaMask' : 'Phantom') : 'Wallet'}
            </Button>
            {!isAnyWalletConnected() && <p className="text-xs text-center text-[hsl(215,16%,56%)] mt-2">Connect wallet above first</p>}
          </CardContent>
        </Card>
      </div>

      {/* History */}
      <Card className="border-[hsl(220,16%,18%)] shadow-none">
        <CardHeader>
          <CardTitle className="text-white font-['Outfit']">Transaction History</CardTitle>
          <CardDescription>Your ZK vault deposits and withdrawals</CardDescription>
        </CardHeader>
        <CardContent>
          {deposits.length > 0 ? (
            <div className="space-y-3">
              {deposits.map((deposit, idx) => (
                <div key={idx} className="p-4 border border-[hsl(220,16%,18%)] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {deposit.status === 'deposited' ? <ArrowDownToLine className="w-4 h-4 text-[#10B981]" /> :
                       deposit.status === 'withdrawn_to_phantom' ? <Wallet className="w-4 h-4 text-[#E8520F]" /> :
                       <ArrowUpFromLine className="w-4 h-4 text-[#00CED1]" />}
                      <span className="font-medium text-white">{deposit.amount} {deposit.token}</span>
                      {deposit.source === 'bot_earnings' && <span className="text-xs px-2 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B] rounded">Bot Earnings</span>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      deposit.status === 'deposited' ? 'bg-green-500/15 text-green-400' :
                      deposit.status === 'withdrawn_to_phantom' ? 'bg-purple-100 text-purple-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>{deposit.status === 'withdrawn_to_phantom' ? 'Phantom' : deposit.status}</span>
                  </div>
                  {deposit.tx_signature && (
                    <a href={deposit.explorer_url || `https://explorer.solana.com/tx/${deposit.tx_signature}?cluster=devnet`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#E8520F] hover:underline flex items-center gap-1">
                      View on Explorer <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <p className="text-xs text-[hsl(215,16%,56%)] font-mono truncate">Note: {deposit.zk_note?.substring(0, 40)}...</p>
                  <p className="text-xs text-[hsl(215,16%,45%)] mt-1">{new Date(deposit.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[hsl(215,16%,56%)]">
              <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No transactions yet</p>
              <p className="text-sm">Make a deposit to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Treasury Status */}
      {treasuryInfo && (
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-gradient-to-r from-[#9945FF] to-[#14F195] flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">S</span>
              </span>
              Solana Treasury
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${treasuryInfo.can_send ? 'bg-green-500/15 text-green-400' : 'bg-amber-100 text-amber-400'}`}>
                {treasuryInfo.can_send ? 'Funded — Live Transfers' : 'Unfunded — Simulated Mode'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <p className="text-xs text-[hsl(215,16%,56%)]">Treasury Address</p>
                <p className="text-xs font-mono text-white">{treasuryInfo.address}</p>
              </div>
              <div>
                <p className="text-xs text-[hsl(215,16%,56%)]">Balance</p>
                <p className="text-sm font-bold text-white">{treasuryInfo.balance_sol} SOL</p>
              </div>
              <a href={treasuryInfo.fund_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#E8520F]/10 text-[#E8520F] text-xs font-medium hover:bg-[#E8520F]/20">
                <ExternalLink className="w-3 h-3" />Fund Treasury
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deposit Modal */}
      <Dialog open={showDepositModal} onOpenChange={(open) => { if (!open) { setShowDepositModal(false); setZkNote(null); } }}>
        <DialogContent className="glass-modal sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white font-['Outfit']">
              <Shield className="w-5 h-5 text-[#E8520F]" />ZK Vault Deposit
            </DialogTitle>
            <DialogDescription className="text-[hsl(215,16%,56%)]">Deposit funds and receive your ZK-Note</DialogDescription>
          </DialogHeader>
          {!zkNote ? (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white">Token</Label>
                <Select value={depositForm.token} onValueChange={(v) => setDepositForm(prev => ({ ...prev, token: v }))}>
                  <SelectTrigger className="border-[hsl(220,16%,18%)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USDT">USDT</SelectItem>
                    <SelectItem value="USDC">USDC</SelectItem>
                    <SelectItem value="ETH">ETH</SelectItem>
                    <SelectItem value="SOL">SOL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white">Amount</Label>
                <Input type="number" value={depositForm.amount} onChange={(e) => setDepositForm(prev => ({ ...prev, amount: parseFloat(e.target.value) }))} className="border-[hsl(220,16%,18%)]" />
              </div>
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-700">Save your ZK-Note securely after deposit. It's the only way to withdraw your funds.</p>
              </div>
              <Button onClick={handleDeposit} disabled={loading} className="w-full bg-[#10B981] hover:bg-[#059669] text-white">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowDownToLine className="w-4 h-4 mr-2" />}
                Confirm Deposit
              </Button>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="font-medium text-green-800">Deposit Successful!</p>
              </div>
              <div className="space-y-2">
                <Label className="text-white">Your ZK-Note (SAVE THIS!)</Label>
                <div className="zk-note p-4 rounded-lg text-sm break-all">{zkNote}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={() => copyToClipboard(zkNote)} variant="outline" className="border-[hsl(220,16%,18%)]">
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}Copy
                </Button>
                <Button onClick={downloadZkNote} className="bg-[#E8520F] hover:bg-[#C9440C] text-white">
                  <Download className="w-4 h-4 mr-2" />Download
                </Button>
              </div>
              <Button onClick={() => { setShowDepositModal(false); setZkNote(null); }} variant="outline" className="w-full border-[hsl(220,16%,18%)]">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Withdraw EVM Modal */}
      <Dialog open={showWithdrawModal} onOpenChange={(open) => { if (!open) { setShowWithdrawModal(false); setEvmWithdrawResult(null); setWithdrawForm({ zk_note: '', recipient_address: '' }); } }}>
        <DialogContent className="glass-modal sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white font-['Outfit']">
              <ArrowUpFromLine className="w-5 h-5 text-[#00CED1]" />Withdraw (EVM)
            </DialogTitle>
            <DialogDescription className="text-[hsl(215,16%,56%)]">Enter your ZK-Note and EVM recipient address</DialogDescription>
          </DialogHeader>
          {!evmWithdrawResult ? (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white">ZK-Note</Label>
                <Input value={withdrawForm.zk_note} onChange={(e) => setWithdrawForm(prev => ({ ...prev, zk_note: e.target.value }))} placeholder="gaurilabs_zk_..." className="font-mono text-sm border-[hsl(220,16%,18%)]" />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Recipient Address (EVM)</Label>
                <Input value={withdrawForm.recipient_address} onChange={(e) => setWithdrawForm(prev => ({ ...prev, recipient_address: e.target.value }))} placeholder="0x..." className="font-mono text-sm border-[hsl(220,16%,18%)]" />
              </div>
              <Button onClick={handleWithdraw} disabled={loading || !withdrawForm.zk_note || !withdrawForm.recipient_address} className="w-full bg-[#00CED1] hover:bg-[#00B3B3] text-white">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpFromLine className="w-4 h-4 mr-2" />}
                Withdraw via Relayer
              </Button>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="font-medium text-green-800">Withdrawal Confirmed!</p>
              </div>
              <div className="p-4 bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg">
                <p className="text-sm text-[#10B981] font-semibold">Funds Received</p>
                <p className="text-2xl font-bold text-white font-mono">${evmWithdrawResult.net_amount?.toFixed(2)}</p>
                <p className="text-xs text-[hsl(215,16%,56%)] font-mono mt-1 break-all">To: {evmWithdrawResult.recipient}</p>
              </div>
              <Button onClick={() => { setShowWithdrawModal(false); setEvmWithdrawResult(null); setWithdrawForm({ zk_note: '', recipient_address: '' }); }} variant="outline" className="w-full border-[hsl(220,16%,18%)]">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Withdraw to Wallet Modal */}
      <Dialog open={showPhantomWithdrawModal} onOpenChange={(open) => { if (!open) { setShowPhantomWithdrawModal(false); setWithdrawResult(null); } }}>
        <DialogContent className="glass-modal sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white font-['Outfit']">
              <Wallet className="w-5 h-5 text-[#E8520F]" />Withdraw to {selectedWalletType === 'metamask' ? 'MetaMask' : 'Phantom'}
            </DialogTitle>
            <DialogDescription className="text-[hsl(215,16%,56%)]">Withdraw directly to your connected wallet</DialogDescription>
          </DialogHeader>
          {!withdrawResult ? (
            <div className="space-y-4 mt-4">
              <div className={`p-3 rounded-lg border ${selectedWalletType === 'metamask' ? 'bg-[#F6851B]/5 border-[#F6851B]/20' : 'bg-[#E8520F]/5 border-[#E8520F]/20'}`}>
                <p className={`text-sm font-medium ${selectedWalletType === 'metamask' ? 'text-[#F6851B]' : 'text-[#E8520F]'}`}>
                  Connected ({selectedWalletType === 'metamask' ? 'EVM' : 'Solana'})
                </p>
                <p className="text-xs text-[hsl(215,16%,56%)] font-mono mt-1 break-all">{getConnectedAddress()}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-white">ZK-Note</Label>
                <Input value={phantomWithdrawForm.zk_note} onChange={(e) => setPhantomWithdrawForm(prev => ({ ...prev, zk_note: e.target.value }))} placeholder="gaurilabs_zk_..." className="font-mono text-sm border-[hsl(220,16%,18%)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Receive as</Label>
                  <Select value={phantomWithdrawForm.crypto} onValueChange={(v) => setPhantomWithdrawForm(prev => ({ ...prev, crypto: v }))}>
                    <SelectTrigger className="border-[hsl(220,16%,18%)]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SOL">SOL (Solana)</SelectItem>
                      <SelectItem value="USDT">USDT</SelectItem>
                      <SelectItem value="USDC">USDC</SelectItem>
                      <SelectItem value="ETH">ETH</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Network</Label>
                  <Select value={phantomWithdrawForm.network} onValueChange={(v) => setPhantomWithdrawForm(prev => ({ ...prev, network: v }))}>
                    <SelectTrigger className="border-[hsl(220,16%,18%)]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="devnet">Devnet</SelectItem>
                      <SelectItem value="testnet">Testnet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="p-3 bg-[#E8520F]/5 rounded-lg border border-[#E8520F]/20">
                <p className="text-xs text-[hsl(215,16%,56%)]">Rate: 1 USD = {cryptoRates[phantomWithdrawForm.crypto]?.rate} {phantomWithdrawForm.crypto}</p>
              </div>
              <Button onClick={handlePhantomWithdraw} disabled={loading || !phantomWithdrawForm.zk_note} className={`w-full text-white ${selectedWalletType === 'metamask' ? 'bg-gradient-to-r from-[#F6851B] to-[#E2761B]' : 'bg-gradient-to-r from-[#AB9FF2] to-[#6366F1]'}`}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wallet className="w-4 h-4 mr-2" />}
                Withdraw {phantomWithdrawForm.crypto}
              </Button>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="font-medium text-green-800">Withdrawal Successful!</p>
              </div>
              <div className="p-4 bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg">
                <p className="text-sm text-[#10B981] font-semibold">Funds Received</p>
                <p className="text-2xl font-bold text-white">{withdrawResult.crypto_amount} {withdrawResult.crypto}</p>
                <p className="text-xs text-[hsl(215,16%,56%)] font-mono mt-1 break-all">To: {withdrawResult.recipient}</p>
              </div>
              <a href={withdrawResult.explorer_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 p-3 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-100 text-sm">
                <ExternalLink className="w-4 h-4" />View on Explorer
              </a>
              <Button onClick={() => { setShowPhantomWithdrawModal(false); setWithdrawResult(null); }} variant="outline" className="w-full border-[hsl(220,16%,18%)]">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
